import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { StockItem } from '../types';

interface Props {
  label: string;
  items: StockItem[];
  selectedPartId?: string;
  emptyLabel: string;
  showQuantity?: boolean;
  onSelect(item?: StockItem): void;
}

function searchValue(value = '') {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('ro').trim();
}

function itemLabel(item: StockItem, showQuantity = false) {
  const quantity = showQuantity ? ` (${item.qty})` : '';
  return `${item.partCode} · ${item.partName}${quantity}`;
}

export function PartCombobox({ label, items, selectedPartId, emptyLabel, showQuantity = false, onSelect }: Props) {
  const inputId = useId();
  const listId = `${inputId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = items.find((item) => item.partId === selectedPartId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(() => selected ? itemLabel(selected, showQuantity) : '');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) setQuery(selected ? itemLabel(selected, showQuantity) : '');
  }, [open, selected, showQuantity]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const filtered = useMemo(() => {
    const needle = searchValue(query);
    const source = needle && selected && query === itemLabel(selected, showQuantity) ? items : items.filter((item) => (
      searchValue(`${item.partCode} ${item.partName}`).includes(needle)
    ));
    return source.slice(0, 60);
  }, [items, query, selected, showQuantity]);

  useEffect(() => { setActiveIndex(0); }, [query]);

  function choose(item?: StockItem) {
    onSelect(item);
    setQuery(item ? itemLabel(item, showQuantity) : '');
    setOpen(false);
  }

  return <div className="part-combobox" ref={rootRef}>
    <label htmlFor={inputId}><span>{label}</span></label>
    <div className="part-combobox__control">
      <input
        id={inputId}
        type="search"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={open && filtered[activeIndex] ? `${inputId}-option-${activeIndex}` : undefined}
        placeholder="Caută după cod sau denumire"
        value={query}
        onFocus={(event) => { setOpen(true); event.currentTarget.select(); }}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault(); setOpen(true); if (filtered.length) setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1));
          } else if (event.key === 'Enter' && open && filtered[activeIndex]) {
            event.preventDefault(); choose(filtered[activeIndex]);
          } else if (event.key === 'Escape') {
            setOpen(false); setQuery(selected ? itemLabel(selected, showQuantity) : '');
          }
        }}
      />
      {(selected || query) && <button type="button" aria-label={emptyLabel} onClick={() => choose(undefined)}>×</button>}
    </div>
    {open && <div className="part-combobox__menu" id={listId} role="listbox">
      <button type="button" className="part-combobox__empty" role="option" aria-selected={!selectedPartId} onPointerDown={(event) => event.preventDefault()} onClick={() => choose(undefined)}>{emptyLabel}</button>
      {filtered.map((item, index) => <button
        type="button"
        id={`${inputId}-option-${index}`}
        role="option"
        aria-selected={item.partId === selectedPartId}
        className={`${index === activeIndex ? 'active' : ''} ${item.partId === selectedPartId ? 'selected' : ''}`}
        key={`${item.partId}:${item.warehouseId || ''}`}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => choose(item)}
      ><strong>{item.partCode}</strong><span>{item.partName}</span>{showQuantity && <small>{item.qty} buc.</small>}</button>)}
      {!filtered.length && <p>Nicio piesă găsită.</p>}
      {filtered.length === 60 && <p>Introdu mai multe caractere pentru a restrânge rezultatele.</p>}
    </div>}
  </div>;
}
